<template>
  <div class="review-comments-container">
    <!-- Comments List with Add Comment Button -->
    <div class="comments-list">
      <div class="comments-header">
        <div v-if="comments.length > 0" class="button-group">
          <button 
            @click="handleDownloadEDL" 
            class="download-button"
            title="Download as EDL for DaVinci Resolve"
          >
            <font-awesome-icon icon="file-lines" /> 
            <span>EDL</span>
          </button>
          <button 
            @click="handleDownloadCSV" 
            class="download-button"
            title="Download as CSV"
          >
            <font-awesome-icon icon="file-csv" /> 
            <span>CSV</span>
          </button>
        </div>

        <button
          v-if="canAddComment() && !editing"
          @click="toggleNewCommentForm" 
          class="add-button"
        >
          <font-awesome-icon icon="plus" /> 
          <span>Add Note</span>
        </button>
      </div>
      
      <div v-if="formError" class="error-message">
        <font-awesome-icon icon="exclamation-triangle" />
        {{ formError }}
      </div>
      
      <!-- New Comment Form (Hidden by default) -->
      <div v-if="showingNewCommentForm" class="comment-form">
        <div class="timestamp-input">
          <input 
            type="number" 
            id="timestamp" 
            v-model="newCommentData.timestamp" 
            step="0.1" 
            min="0" 
            class="form-control time-input"
            placeholder="Time"
          />
          <span class="formatted-time">{{ formatTimecode(newCommentData.timestamp || 0) }}</span>
        </div>
        <textarea 
          v-model="newCommentData.text" 
          class="form-control" 
          rows="2"
          placeholder="Write your note"
          ref="activeTextarea"
          @keydown="handleCommentKeydown"
        ></textarea>
        <div class="form-actions">
          <button 
            @click="addComment" 
            class="submit-button" 
            :disabled="!isFormValid || isSubmitting"
          >
            <font-awesome-icon v-if="isSubmitting" icon="spinner" spin />
            <span v-else>Submit</span>
          </button>
          <button @click="toggleNewCommentForm" class="cancel-button">
            Cancel
          </button>
        </div>
      </div>

      <LoadingIndicator v-if="isLoading">Loading comments...</LoadingIndicator>
      <div v-else-if="comments.length === 0" class="empty-message">
        <font-awesome-icon icon="comment-slash" />
        No notes yet
      </div>
      
      <div v-else class="comments-timeline">
        <!-- Comments sorted by timestamp -->
        <div 
          v-for="comment in sortedComments" 
          :key="comment.id" 
          class="comment-thread"
          :class="{'has-replies': (comment.replies && comment.replies.length > 0) || replyingToCommentId === comment.id}"
        >
          <div class="comment-item" :class="{ 'is-editing': editingCommentId === comment.id }">
            <div class="comment-content">
              <div class="comment-header">
                <div class="comment-author">
                  {{ comment.user_first_name }} {{ comment.user_last_name }}
                </div>
                <div class="comment-date">
                  {{ formatDatetime(comment.created_at) }}
                </div>
              </div>
              
              <div v-if="editingCommentId === comment.id" class="comment-edit-form">
                <textarea 
                  v-model="editCommentText" 
                  class="form-control" 
                  rows="2"
                  ref="activeTextarea"
                  @keydown="handleCommentKeydown"
                ></textarea>
                <div class="form-actions">
                  <button @click="updateComment(comment.id)" class="submit-button">
                    Save
                  </button>
                  <button @click="cancelEdit" class="cancel-button">
                    Cancel
                  </button>
                </div>
              </div>
              <div v-else class="comment-text">
                <span 
                  class="comment-timestamp clickable" 
                  @click="handleTimestampClick(comment.timestamp)"
                  title="Jump to timecode"
                >
                  {{ formatTimecode(comment.timestamp) }}
                </span>
                {{ comment.text }}
              </div>
              
              <div class="comment-actions" v-if="!editingCommentId">
                <button 
                  v-if="canAddComment() && !editing"
                  @click="startReply(comment)" 
                  class="action-button edit"
                  title="Reply to comment"
                >
                  <font-awesome-icon icon="plus" />
                </button>
                <button 
                  v-if="canEditComment(comment) && !editing"
                  @click="startEdit(comment)" 
                  class="action-button edit"
                  title="Edit"
                >
                  <font-awesome-icon icon="edit" />
                </button>
                <button 
                  v-if="canDeleteComment(comment) && !editing"
                  @click="deleteComment(comment.id)" 
                  class="action-button delete"
                  title="Delete"
                >
                  <font-awesome-icon icon="trash" />
                </button>
              </div>
            </div>
          </div>
          
          <!-- Replies -->
          <div v-if="comment.replies && comment.replies.length > 0" class="comment-replies">
            <div 
              v-for="reply in comment.replies" 
              :key="reply.id" 
              class="comment-item reply-item"
              :class="{ 'is-editing': editingCommentId === reply.id }"
            >
              <div class="comment-content">
                <div class="comment-header">
                  <div class="comment-author">
                    {{ reply.user_first_name }} {{ reply.user_last_name }}
                  </div>
                  <div class="comment-date">
                    {{ formatDatetime(reply.created_at) }}
                  </div>
                </div>
                
                <div v-if="editingCommentId === reply.id" class="comment-edit-form">
                  <textarea 
                    v-model="editCommentText" 
                    class="form-control" 
                    rows="2"
                    ref="activeTextarea"
                    @keydown="handleCommentKeydown"
                  ></textarea>
                  <div class="form-actions">
                    <button @click="updateComment(reply.id)" class="submit-button">
                      Save
                    </button>
                    <button @click="cancelEdit" class="cancel-button">
                      Cancel
                    </button>
                  </div>
                </div>
                <div v-else class="comment-text">
                  {{ reply.text }}
                </div>
                
                <div class="comment-actions" v-if="!editing">
                  <button 
                    v-if="canEditComment(reply)"
                    @click="startEdit(reply)" 
                    class="action-button edit"
                    title="Edit"
                  >
                    <font-awesome-icon icon="edit" />
                  </button>
                  <button 
                    v-if="canDeleteComment(reply)"
                    @click="deleteComment(reply.id)" 
                    class="action-button delete"
                    title="Delete"
                  >
                    <font-awesome-icon icon="trash" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Reply Form -->
          <div v-if="replyingToCommentId === comment.id" class="reply-form">
            <textarea 
              v-model="replyText" 
              class="form-control" 
              rows="2"
              placeholder="Write your reply"
              ref="activeTextarea"
              @keydown="handleCommentKeydown"
            ></textarea>
            <div class="form-actions">
              <button 
                @click="addReply()" 
                class="submit-button"
                :disabled="!replyText.trim() || isSubmitting"
              >
                <font-awesome-icon v-if="isSubmitting" icon="spinner" spin />
                <span v-else>Submit</span>
              </button>
              <button @click="cancelReply" class="cancel-button">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { FontAwesomeIcon } from '@/scripts/icons';
import { isSuperuser, canReviewVideo, isOwner } from '@/scripts/permissions';
import { authState } from '@/scripts/services/authService';
import { handleApiError, formatTimecode, formatDatetime } from '@/scripts/shared-utils';
import { downloadAsEDL, downloadAsCSV } from '@/scripts/comment-serializer';
import { getVideoFileComments, createVideoFileComment, videoFileKeys } from '@/scripts/api/videoFileApi';
import { createReply, updateComment as updateCommentApi, deleteComment as deleteCommentApi } from '@/scripts/api/commentApi';
import LoadingIndicator from '@/components/LoadingIndicator.vue';

const props = defineProps({
  videoFile: {
    type: Object,
    required: true
  },
  videoPlayer: {
    type: Object,
    default: null
  }
});

// State
const queryClient = useQueryClient();

const formError = ref('');
const isSubmitting = ref(false);

const showingNewCommentForm = ref(false);
const newCommentData = ref({
  text: '',
  timestamp: null
});
const editingCommentId = ref(null);
const editCommentText = ref('');
const replyingToCommentId = ref(null);
const replyText = ref('');
const activeTextarea = ref(null);

// Computed properties
const editing = computed(() => editingCommentId.value !== null || replyingToCommentId.value !== null || showingNewCommentForm.value);
const videoFileId = computed(() => props.videoFile?.id);

// Permissions
const organizationId = computed(() => props.videoFile?.organization_id);
function canAddComment() {
  return canReviewVideo(organizationId.value);
}

function canEditComment(c) {
  const isAuthor = c.user_id === authState.userId.value;
  return (isAuthor && canAddComment()) || isSuperuser();
}

function canDeleteComment(c) {
  const isAuthor = c.user_id === authState.userId.value;
  return (isAuthor && canAddComment()) || isOwner(organizationId.value) || isSuperuser();
}

// Fetch comments
const commentsQueryKey = computed(() => videoFileKeys.getVideoFileComments(videoFileId.value));
const { data: comments, isLoading } = useQuery({
  queryKey: commentsQueryKey,
  queryFn: () => getVideoFileComments(videoFileId.value),
  enabled: computed(() => !!videoFileId.value),
  staleTime: 60000,
  placeholderData: [],
  onError: (error) => handleApiError(error, 'loading comments')
});

const sortedComments = computed(() => {
  // Filter top-level comments (no parent_id)
  const topLevelComments = comments.value.filter(c => !c.parent_id);
  
  // Sort by timestamp
  const sorted = [...topLevelComments].sort((a, b) => {
    return a.timestamp - b.timestamp;
  });
  
  // Add replies to each comment
  return sorted.map(comment => {
    const replies = comments.value.filter(c => c.parent_id === comment.id);
    return {
      ...comment,
      replies: replies.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    };
  });
});

// Methods
const handleCommentKeydown = (event) => {
  // Check for Cmd+Enter (Mac) or Ctrl+Enter (Windows/Linux)
  if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
    // Determine which form is active based on component state
    if (showingNewCommentForm.value) {
      addComment();
    } else if (replyingToCommentId.value) {
      addReply();
    } else if (editingCommentId.value) {
      updateComment();
    }
  }
};

const handleTimestampClick = (timestamp) => {
  if (props.videoPlayer) {
    props.videoPlayer.seek(timestamp);
    
    // Scroll to the video player
    const playerElement = props.videoPlayer.$el;
    if (playerElement) {
      playerElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
};

const focusTextarea = () => {
  setTimeout(() => {
    if (activeTextarea.value) {
      const textarea = Array.isArray(activeTextarea.value) ? activeTextarea.value[0] : activeTextarea.value;
      
      if (textarea) {
        textarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
        textarea.focus();
      }
    }
  }, 100);
};

// Create comment
const toggleNewCommentForm = () => {
  showingNewCommentForm.value = !showingNewCommentForm.value;
  if (showingNewCommentForm.value) {
    // Reset form
    newCommentData.value = {
      text: '',
      timestamp: null
    };
    formError.value = '';
    
    // Get current time from video player if available, with max 3 decimal places
    if (props.videoPlayer) {
      const currentTime = props.videoPlayer.getCurrentTime();
      if (currentTime !== undefined) {
        newCommentData.value.timestamp = Math.round(currentTime * 1000) / 1000;
      }
    }
    
    focusTextarea();
  }
};

const isFormValid = computed(() => {
  return newCommentData.value.text.trim() && 
         newCommentData.value.timestamp !== null && 
         newCommentData.value.timestamp >= 0;
});

const createCommentMutation = useMutation({
  mutationFn: (commentData) => createVideoFileComment(videoFileId.value, commentData),
  onSuccess: () => {
    // Invalidate and refetch comments
    queryClient.invalidateQueries({ queryKey: commentsQueryKey });
    // Reset form
    newCommentData.value = { text: '', timestamp: null };
    showingNewCommentForm.value = false;
  },
  onError: (err) => {
    handleApiError(err, 'adding comment');
    formError.value = err.response?.data?.error || 'Failed to add comment';
  }
});

const addComment = () => {
  formError.value = '';
  
  if (!isFormValid.value) {
    formError.value = 'Please enter both a comment and a valid timestamp';
    return;
  }
  
  isSubmitting.value = true;
  createCommentMutation.mutate({
    text: newCommentData.value.text,
    timestamp: parseFloat(newCommentData.value.timestamp)
  }, {
    onSettled: () => {
      isSubmitting.value = false;
    }
  });
};

// Edit comment
const startEdit = (comment) => {
  editingCommentId.value = comment.id;
  editCommentText.value = comment.text;
  // Cancel any active reply
  replyingToCommentId.value = null;
  
  focusTextarea();
};

const cancelEdit = () => {
  editingCommentId.value = null;
  editCommentText.value = '';
};

const updateCommentMutation = useMutation({
  mutationFn: ({ commentId, commentData }) => updateCommentApi(commentId, commentData),
  onSuccess: () => {
    // Invalidate and refetch comments
    queryClient.invalidateQueries({ queryKey: commentsQueryKey });
    // Reset edit state
    editingCommentId.value = null;
    editCommentText.value = '';
  },
  onError: (err) => {
    handleApiError(err, 'updating comment');
    formError.value = err.response?.data?.error || 'Failed to update comment';
  }
});

const updateComment = () => {
  if (!editCommentText.value.trim() || !editingCommentId.value) {
    return;
  }
  
  isSubmitting.value = true;
  const commentId = editingCommentId.value;

  updateCommentMutation.mutate({
    commentId,
    commentData: { text: editCommentText.value }
  }, {
    onSettled: () => {
      isSubmitting.value = false;
    }
  });
};

// Delete comment
const deleteCommentMutation = useMutation({
  mutationFn: (commentId) => deleteCommentApi(commentId),
  onSuccess: () => {
    // Invalidate and refetch comments
    queryClient.invalidateQueries({ queryKey: commentsQueryKey });
  },
  onError: (err) => {
    handleApiError(err, 'deleting comment');
    formError.value = err.response?.data?.error || 'Failed to delete comment';
  }
});

const deleteComment = (commentId) => {
  if (!confirm('Are you sure you want to delete this comment?')) {
    return;
  }
  
  deleteCommentMutation.mutate(commentId);
};

// Create Reply
const startReply = (comment) => {
  replyingToCommentId.value = comment.id;
  replyText.value = '';
  // Cancel any active edit
  editingCommentId.value = null;
  
  focusTextarea();
};

const cancelReply = () => {
  replyingToCommentId.value = null;
  replyText.value = '';
};

const createReplyMutation = useMutation({
  mutationFn: ({ parentId, replyData }) => createReply(parentId, replyData),
  onSuccess: () => {
    // Invalidate and refetch comments
    queryClient.invalidateQueries({ queryKey: commentsQueryKey });
    // Reset reply state
    replyingToCommentId.value = null;
    replyText.value = '';
  },
  onError: (err) => {
    handleApiError(err, 'adding reply');
    formError.value = err.response?.data?.error || 'Failed to add reply';
  }
});

const addReply = () => {
  if (!replyText.value.trim() || !replyingToCommentId.value) {
    return;
  }
  
  isSubmitting.value = true;
  const parentId = replyingToCommentId.value;
  
  createReplyMutation.mutate({
    parentId,
    replyData: { text: replyText.value }
  }, {
    onSettled: () => {
      isSubmitting.value = false;
    }
  });
};

// Download buttons
const handleDownloadEDL = () => {
  if (comments.value.length === 0) return;
  if (!props.videoFile) return;
  
  try {
    downloadAsEDL(comments.value, props.videoFile);
  } catch (err) {
    console.error('Error downloading EDL file:', err);
    formError.value = 'Failed to download EDL file';
  }
};

const handleDownloadCSV = () => {
  if (comments.value.length === 0) return;
  if (!props.videoFile) return;
  
  try {
    downloadAsCSV(comments.value, props.videoFile);
  } catch (err) {
    console.error('Error downloading CSV file:', err);
    formError.value = 'Failed to download CSV file';
  }
};
</script>

<style scoped>
@import '@/styles/review-comments.css';
@import '@/styles/shared-form.css';
@import '@/styles/shared-buttons.css';
</style>